import {
  Title,
  Text,
  TextInput,
  NumberInput,
  Button,
  Group,
  Stack,
  ActionIcon,
  Alert,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { normalizeErrorString } from '@medplum/core'
import { IconCircleOff, IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react'
import { useForm } from '@mantine/form'
import { Navigation } from './components'
import { DEFAULT_PROCEDURES } from './defaultProcedures'
import { buildProcedureMeta } from '@/utils/terminologies'

type TerminologyItem = {
  code: string
  name: string
  price?: number
  /** NP/PA amount (PD-1597); the fee policy's percentage applies when absent. */
  priceMidlevel?: number
}

type TerminologyFormValues = {
  items: TerminologyItem[]
}

const CODE_ALLOWED_REGEX = /^[A-Za-z0-9._\-/+ ]+$/
const NAME_ALLOWED_REGEX = /^[A-Za-z0-9 .,'():;\-/&+‐‑‒–—―]+$/

const hasAllowedSymbols = (value: string, type: 'code' | 'name'): boolean => {
  const trimmed = value.trim()
  if (!trimmed) {
    return false
  }

  return type === 'code' ? CODE_ALLOWED_REGEX.test(trimmed) : NAME_ALLOWED_REGEX.test(trimmed)
}

const getReferenceId = (reference?: string): string | undefined => {
  if (!reference) return undefined
  const parts = reference.split('/')
  return parts[parts.length - 1]
}

export function SettingsTerminology() {
  const medplum = useMedplum()
  const [isSaving, setIsSaving] = useState(false)
  const [isRoleLoading, setIsRoleLoading] = useState(true)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const profile = medplum.getProfile()

  const form = useForm<TerminologyFormValues>({
    mode: 'uncontrolled',
    initialValues: {
      items: [{ code: '', name: '', price: undefined, priceMidlevel: undefined }],
    },
    validate: {
      items: {
        code: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.items[index]
          const hasAnyValue = Boolean(
            row?.code?.trim() || row?.name?.trim() || row?.price !== undefined,
          )

          // Allow empty placeholder rows.
          if (!hasAnyValue) {
            return null
          }

          if (!value.trim()) {
            return 'Code is required'
          }
          if (!hasAllowedSymbols(value, 'code')) {
            return 'Code contains unsupported symbols'
          }
          return null
        },
        name: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.items[index]
          const hasAnyValue = Boolean(
            row?.code?.trim() || row?.name?.trim() || row?.price !== undefined,
          )

          // Allow empty placeholder rows.
          if (!hasAnyValue) {
            return null
          }

          if (!value.trim()) {
            return 'Name is required'
          }
          if (!hasAllowedSymbols(value, 'name')) {
            return 'Name contains unsupported symbols'
          }
          return null
        },
      },
    },
  })

  useEffect(() => {
    let isActive = true

    const loadRole = async () => {
      setIsRoleLoading(true)
      try {
        if (!profile?.id) {
          if (isActive) {
            setOrganizationId(null)
            setIsRoleLoading(false)
          }
          return
        }

        const role = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        const orgRef = role?.organization?.reference
        const orgId = getReferenceId(orgRef)
        if (isActive) {
          setOrganizationId(orgId ?? null)
        }
      } catch (err) {
        if (!isActive) return
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'Error',
          message: normalizeErrorString(err) || 'Failed to load practitioner role.',
        })
        setOrganizationId(null)
      } finally {
        if (isActive) {
          setIsRoleLoading(false)
        }
      }
    }

    loadRole()

    return () => {
      isActive = false
    }
  }, [medplum, profile?.id])

  useEffect(() => {
    let isActive = true

    const loadTerminology = async () => {
      if (!organizationId) return
      try {
        const { name } = buildProcedureMeta(organizationId)
        const codeSystem = await medplum.searchOne('CodeSystem', `name=${name}`)
        if (!isActive || !codeSystem?.concept?.length) return

        const items = codeSystem.concept.map((concept) => {
          const priceProp = concept.property?.find((prop) => prop.code === 'price')
          const price =
            typeof priceProp?.valueDecimal === 'number' ? priceProp.valueDecimal : undefined
          const midlevelProp = concept.property?.find((prop) => prop.code === 'priceMidlevel')
          const priceMidlevel =
            typeof midlevelProp?.valueDecimal === 'number' ? midlevelProp.valueDecimal : undefined
          return {
            code: concept.code,
            name: concept.display ?? '',
            price,
            priceMidlevel,
          }
        })

        form.setValues({ items })
      } catch (err) {
        if (!isActive) return
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'Error',
          message: normalizeErrorString(err) || 'Failed to load terminology.',
        })
      }
    }

    loadTerminology()

    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, organizationId])

  const addItem = () => {
    form.insertListItem('items', { code: '', name: '', price: undefined, priceMidlevel: undefined })
  }

  const removeItem = (index: number) => {
    form.removeListItem('items', index)
  }

  const restoreDefaults = () => {
    form.setValues({
      items: DEFAULT_PROCEDURES.map((item) => ({ ...item })),
    })
  }

  const handleSubmit = async (values: TerminologyFormValues) => {
    if (isSaving) return
    if (!organizationId) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: 'Organization not found.',
      })
      return
    }

    setIsSaving(true)
    const items = values.items
      .map((item) => ({
        code: item.code.trim(),
        name: item.name.trim(),
        price: item.price,
        priceMidlevel: item.priceMidlevel,
      }))
      .filter((item) => item.code.length > 0)

    if (items.length === 0) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: 'Add at least one item with a code.',
      })
      setIsSaving(false)
      return
    }

    const invalidIndex = items.findIndex(
      (item) => !hasAllowedSymbols(item.code, 'code') || !hasAllowedSymbols(item.name, 'name'),
    )

    if (invalidIndex !== -1) {
      const bad = items[invalidIndex]
      const badCode = !hasAllowedSymbols(bad.code, 'code')
      const badName = !hasAllowedSymbols(bad.name, 'name')

      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: `Validation error (row ${invalidIndex + 1})`,
        message: [
          badCode ? `Code "${bad.code}" has unsupported symbols.` : '',
          badName ? `Name "${bad.name}" has unsupported symbols.` : '',
        ]
          .filter(Boolean)
          .join(' '),
      })
      setIsSaving(false)
      return
    }

    const { name, codeSystemUrl, valueSetUrl } = buildProcedureMeta(organizationId)

    try {
      const concepts = items.map((item) => ({
        code: item.code,
        display: item.name || item.code,
        property: [
          ...(typeof item.price === 'number' ? [{ code: 'price', valueDecimal: item.price }] : []),
          ...(typeof item.priceMidlevel === 'number'
            ? [{ code: 'priceMidlevel', valueDecimal: item.priceMidlevel }]
            : []),
          { code: 'currency', valueCode: 'USD' },
        ],
      }))

      const existingCodeSystem = await medplum.searchOne('CodeSystem', `name=${name}`)
      if (existingCodeSystem) {
        // Medplum rejects a concept property the CodeSystem does not declare — catalogs
        // created before the NP/PA price existed need the declaration added (PD-1597).
        const declared = existingCodeSystem.property ?? []
        const property = declared.some((p) => p.code === 'priceMidlevel')
          ? declared
          : [
              ...declared,
              { code: 'priceMidlevel', description: 'NP/PA price', type: 'decimal' as const },
            ]
        await medplum.updateResource({
          ...existingCodeSystem,
          name,
          url: codeSystemUrl,
          property,
          concept: concepts,
        })
      } else {
        await medplum.createResource({
          resourceType: 'CodeSystem',
          status: 'active',
          content: 'complete',
          name,
          title: 'BeHuman Procedures',
          url: codeSystemUrl,
          property: [
            {
              code: 'price',
              description: 'Base cash price',
              type: 'decimal',
            },
            {
              code: 'priceMidlevel',
              description: 'NP/PA price',
              type: 'decimal',
            },
            {
              code: 'currency',
              description: 'ISO currency code',
              type: 'code',
            },
          ],
          concept: concepts,
        })
      }

      const existingValueSet = await medplum.searchOne('ValueSet', `name=${name}`)
      if (existingValueSet) {
        await medplum.updateResource({
          ...existingValueSet,
          name,
          url: valueSetUrl,
          compose: {
            include: [
              {
                system: codeSystemUrl,
              },
            ],
          },
        })
      } else {
        await medplum.createResource({
          resourceType: 'ValueSet',
          status: 'active',
          name,
          title: 'BeHuman Procedures',
          url: valueSetUrl,
          compose: {
            include: [
              {
                system: codeSystemUrl,
              },
            ],
          },
        })
      }

      showNotification({
        color: 'green',
        title: 'Saved',
        message: 'Terminology saved successfully.',
      })
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(err) || 'Failed to save terminology.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div>
      <Navigation />
      <div className="px-6 py-4">
        <Title order={2}>Terminology</Title>

        {isRoleLoading && (
          <Text c="dimmed" className="mt-4">
            Loading organization…
          </Text>
        )}

        {!isRoleLoading && !organizationId && (
          <Alert
            className="mt-4 w-full max-w-[800px]"
            color="yellow"
            icon={<IconInfoCircle size={16} />}
            title="Organization required"
          >
            This feature is available for organization members. Switch to an organization account to
            manage procedures.
          </Alert>
        )}

        {!isRoleLoading && organizationId && (
          <div className="mt-6 w-full max-w-[800px] rounded-md border border-gray-200 bg-white p-6 shadow-xs">
            <Title order={4}>Procedures for billing</Title>
            <form
              onSubmit={(event) => {
                event.preventDefault()
                void handleSubmit(form.getValues())
              }}
            >
              <Stack gap="md" className="mt-4">
                {form.getValues().items.map((_, index) => (
                  <Group key={index} align="end" gap="sm" wrap="nowrap">
                    <span>{index + 1}</span>
                    <TextInput
                      key={form.key(`items.${index}.code`)}
                      label="Code"
                      placeholder="CPT or custom code"
                      {...form.getInputProps(`items.${index}.code`)}
                      className="flex-1"
                    />
                    <TextInput
                      key={form.key(`items.${index}.name`)}
                      label="Name"
                      placeholder="Procedure name"
                      {...form.getInputProps(`items.${index}.name`)}
                      className="flex-2"
                    />
                    <NumberInput
                      key={form.key(`items.${index}.price`)}
                      label="Price"
                      placeholder="Optional"
                      leftSection="$"
                      leftSectionWidth={24}
                      min={0}
                      thousandSeparator=","
                      decimalScale={2}
                      {...form.getInputProps(`items.${index}.price`)}
                      className="flex-1"
                    />
                    <NumberInput
                      key={form.key(`items.${index}.priceMidlevel`)}
                      label="NP/PA price"
                      placeholder="Optional"
                      leftSection="$"
                      leftSectionWidth={24}
                      min={0}
                      thousandSeparator=","
                      decimalScale={2}
                      {...form.getInputProps(`items.${index}.priceMidlevel`)}
                      className="flex-1"
                    />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => removeItem(index)}
                      aria-label="Remove item"
                      disabled={form.getValues().items.length === 1 || isSaving}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}

                <Group justify="space-between">
                  <Group gap="sm">
                    <Button
                      type="button"
                      variant="light"
                      leftSection={<IconPlus size={16} />}
                      onClick={addItem}
                      disabled={isSaving}
                    >
                      Add item
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={restoreDefaults}
                      disabled={isSaving}
                    >
                      Restore default procedures
                    </Button>
                  </Group>
                  <Button
                    type="button"
                    onClick={() => {
                      void handleSubmit(form.getValues())
                    }}
                    loading={isSaving}
                  >
                    Submit
                  </Button>
                </Group>
              </Stack>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
